import { Link } from "@/i18n/navigation";
import Image from "next/image";

// Adapter function to normalize different location data structures
function normalizeLocationData(rawItem) {
  if (!rawItem) return null;

  // Check if it's CityLandingPage API structure (getGetCityNeighbors)
  if (rawItem.Title && rawItem.ImageUrl && rawItem.FlightUrl) {
    return {
      redirectUrl: rawItem.FlightUrl.UrlOnlyDestination || "#",
      imageUrl: rawItem.ImageUrl,
      imageAlt: rawItem.AnchorTitle || rawItem.Title,
      title: rawItem.Title || rawItem.Name || rawItem.Display,
      subtitle: rawItem.SubTitle,
    };
  }

  // Check if it's FlightTicket page API structure (getPopularRoutes)
  if (rawItem.Title && rawItem.IconImage && rawItem.RedirectUrl) {
    return {
      redirectUrl: rawItem.RedirectUrl || "#",
      imageUrl: rawItem.IconImage.ImageUrl,
      imageAlt: rawItem.IconImage.ImageAlt || rawItem.Title,
      title: rawItem.Title || rawItem.Name || rawItem.Display,
      subtitle: null,
    };
  }

  // Check if it's legacy structure (RedirectUrl at root level)
  if (rawItem.RedirectUrl && rawItem.IconImage) {
    return {
      redirectUrl: rawItem.RedirectUrl || "#",
      imageUrl: rawItem.IconImage.ImageUrl,
      imageAlt: rawItem.IconImage.ImageAlt || "Location",
      title: rawItem.Title || rawItem.Name || rawItem.Display || "Location",
      subtitle: null,
    };
  }

  // Fallback for unknown structure
  const redirectUrl = rawItem?.RedirectUrl || rawItem?.redirectUrl;
  return {
    redirectUrl: redirectUrl || "#",
    imageUrl:
      rawItem?.IconImage?.ImageUrl ||
      rawItem?.imageUrl ||
      rawItem?.ImageUrl ||
      "/images/default-location.jpg",
    imageAlt:
      rawItem?.IconImage?.ImageAlt ||
      rawItem?.imageAlt ||
      rawItem?.ImageAlt ||
      "Location",
    title: rawItem?.Title || rawItem?.title || rawItem?.Display || rawItem?.Name || "Location",
    subtitle: rawItem?.SubTitle || rawItem?.subtitle || null,
  };
}

/**
 * CTA tıklamasında dataLayer'a push eder (cta_click).
 * dataLayerPayload section config'den gelir (PageBuilder); cta_button kart başlığıdır.
 */
function pushCtaClickDataLayer(dataLayerPayload, cta_button) {
  if (typeof window === "undefined" || !dataLayerPayload) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ...dataLayerPayload,
    cta_button: cta_button ?? "",
  });
}

export default function PopularLocationCard({ item = {}, dataLayerPayload } = {}) {
  // Normalize the item data using adapter
  const normalizedData = normalizeLocationData(item);

  // If no valid item data, show a fallback
  if (
    !normalizedData ||
    !normalizedData.redirectUrl ||
    normalizedData.redirectUrl === "#"
  ) {
    return (
      <div className="mr-4 flex shrink-0 lg:mr-6">
        <div className="relative aspect-[230/240] w-[230px] overflow-hidden rounded-lg bg-gray-200 shadow-sm lg:w-[214px] xl:w-[230px]">
          <div className="flex h-full items-center justify-center">
            <p className="text-gray-500">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  const { redirectUrl, imageUrl, imageAlt, title, subtitle } = normalizedData;

  const handleClick = () => {
    if (dataLayerPayload) {
      pushCtaClickDataLayer(dataLayerPayload, title);
    }
  };

  return (
    <Link
      href={redirectUrl}
      title={title}
      className="mr-4 flex shrink-0 lg:mr-6"
      onClick={handleClick}
    >
      <div className="relative aspect-[230/240] w-[230px] overflow-hidden rounded-lg shadow-sm lg:w-[214px] xl:w-[230px]">
        <Image
          src={imageUrl || "/images/dummy/city-card-5.jpg"}
          alt={imageAlt}
          fill
          className="object-cover"
          sizes="(max-width: 1024px) 230px, (max-width: 1280px) 214px, 230px"
        />
        <div className="gradient-overlay absolute inset-0" />
        <div className="absolute bottom-6 left-6 z-10 text-white">
          <h3 className="text-xl font-semibold">{title}</h3>
        </div>
      </div>
    </Link>
  );
}
